// 引入 React 运行时。
import React from "react";
// 引入 className 合并工具。
import cx from "classnames";
// 引入帖子列表组件。
import UserPostList from "@/pages/user-center/components/UserPostList";
// 引入消息列表组件。
import MessageList from "@/pages/user-center/components/MessageList";
import { IThreadLisItem } from "@/api/thread/list";
import { USER_ACTION_TYPE } from "@/components/UserAction";
// 引入空态图标。
import EmptyLikeIcon from "../../../assets/icon/account/file-list-2-fill.svg";
// 引入样式。
import styles from "@/pages/user-center/index.module.scss";

// PC tab 内容渲染入参类型。
interface RenderTabContentParams {
  tabKey: string;
  isSelfView: boolean;
  t: (key: string) => string;
  showDrafts: boolean;
  showPosts: boolean;
  drafts: any[];
  publishedPosts: any[];
  postsLoading: boolean;
  handleItemUpdate: any;
  likesInitialized: boolean;
  likesData: any[];
  likesLoading: boolean;
  collectionsInitialized: boolean;
  collectionsData: any[];
  collectionsLoading: boolean;
  messagesInitialized: boolean;
  messagesData: any[];
  messagesLoading: boolean;
  setShowPointsExchangeModal: (val: boolean) => void;
}

// 根据当前 tab 渲染对应内容区域。
export const renderPcTabContent = ({
  tabKey,
  isSelfView,
  t,
  showDrafts,
  showPosts,
  drafts,
  publishedPosts,
  postsLoading,
  handleItemUpdate,
  likesInitialized,
  likesData,
  likesLoading,
  collectionsInitialized,
  collectionsData,
  collectionsLoading,
  messagesInitialized,
  messagesData,
  messagesLoading,
  setShowPointsExchangeModal,
}: RenderTabContentParams) => {
  // 当前页面名用于埋点字段。
  const currentPageName = isSelfView ? "MyProfilePage" : "UserProfilePage";
  // 按 tabKey 走分支渲染。
  switch (tabKey) {
    case "myPosts":
      if (!showDrafts && !showPosts) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIcon}>
              <EmptyLikeIcon />
            </div>
            <p className={styles.emptyText}>
              {isSelfView ? (
                <>
                  {t("user_center_empty_works_line1")}
                  <br />
                  {t("user_center_empty_works_line2")}
                </>
              ) : (
                t("user_center_pc_ta_no_works")
              )}
            </p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          {showDrafts && (
            <div className={styles.section}>
              <UserPostList data={drafts} isDraft={true} onItemUpdate={handleItemUpdate} pageName={currentPageName} />
            </div>
          )}
          {showPosts && (
            <div className={styles.section}>
              <UserPostList
                data={publishedPosts}
                loading={postsLoading}
                onItemUpdate={handleItemUpdate}
                pageName={currentPageName}
                listName={isSelfView ? t("user_center_pc_my_posts") : undefined}
              />
            </div>
          )}
        </div>
      );
    case "myLikes":
      if (likesInitialized && likesData.length === 0 && !likesLoading) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIcon}>
              <EmptyLikeIcon />
            </div>
            <p className={styles.emptyText}>
              {t("user_center_pc_empty_likes_line1")}
              <br />
              {t("user_center_pc_empty_likes_tip")}
            </p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          <div className={styles.section}>
            <UserPostList
              data={likesData}
              loading={likesLoading}
              onItemUpdate={handleItemUpdate}
              pageName={currentPageName}
              listName={isSelfView ? t("user_center_pc_my_likes") : undefined}
            />
          </div>
        </div>
      );
    case "myCollections":
      if (collectionsInitialized && collectionsData.length === 0 && !collectionsLoading) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIcon}>
              <EmptyLikeIcon />
            </div>
            <p className={styles.emptyText}>
              {t("user_center_pc_empty_collections_line1")}
              <br />
              {t("user_center_pc_empty_collections_tip")}
            </p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          <div className={styles.section}>
            <UserPostList
              data={collectionsData}
              loading={collectionsLoading}
              onItemUpdate={handleItemUpdate}
              pageName={currentPageName}
              listName={isSelfView ? t("user_center_pc_my_collections") : undefined}
            />
          </div>
        </div>
      );
    case "messages":
      if (messagesInitialized && messagesData.length === 0 && !messagesLoading) {
        return (
          <div className={cx(styles.emptyState, styles.likeEmptyState)}>
            <div className={styles.emptyIcon}>
              <EmptyLikeIcon />
            </div>
            <p className={styles.emptyText}>{t("user_center_pc_no_messages")}</p>
          </div>
        );
      }
      return (
        <div className={styles.tabContent}>
          <div className={styles.section}>
            <MessageList
              data={messagesData}
              loading={messagesLoading}
              onOpenPointsExchange={() => setShowPointsExchangeModal(true)}
            />
          </div>
        </div>
      );
    default:
      // 未知 tab 返回空。
      return null;
  }
};

// 判断当前 tab 是否命中“已初始化且空数据”的空态条件。
export const isPcTabContentEmpty = ({
  activeTab,
  showDrafts,
  showPosts,
  likesInitialized,
  likesData,
  likesLoading,
  collectionsInitialized,
  collectionsData,
  collectionsLoading,
  messagesInitialized,
  messagesData,
  messagesLoading,
}: any) => {
  switch (activeTab) {
    case "myPosts":
      return !showDrafts && !showPosts;
    case "myLikes":
      return likesInitialized && likesData.length === 0 && !likesLoading;
    case "myCollections":
      return collectionsInitialized && collectionsData.length === 0 && !collectionsLoading;
    case "messages":
      return messagesInitialized && messagesData.length === 0 && !messagesLoading;
    default:
      return true;
  }
};
